import { Injectable } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, IRowNode } from 'ag-grid-community';
import { DeclineCategoryRow, DeclinePortal, DeclineReasonMappingCell } from '../interfaces/decline-reason-mapping.interface';
import { DeclineMappingCategoryCellComponent } from '../cell-renderers/decline-mapping-category-cell.component';
import { DeclineMappingPortalCellComponent } from '../cell-renderers/decline-mapping-portal-cell.component';
import { DeclineMappingDeleteCellComponent } from '../cell-renderers/decline-mapping-delete-cell.component';

@Injectable()
export class DeclineMappingGridCoordinatorService {
  private gridApi: GridApi<DeclineCategoryRow> | null = null;

  onGridReady(ev: GridReadyEvent<DeclineCategoryRow>): void {
    this.gridApi = ev.api;
  }

  setInitialRows(rows: DeclineCategoryRow[]): void {
    this.gridApi?.setGridOption('rowData', rows);
  }

  addRow(row: DeclineCategoryRow): void {
    this.gridApi?.applyTransaction({ add: [row] });
  }

  removeRow(rowId: string): void {
    const node = this.gridApi?.getRowNode(rowId);
    if (node?.data) this.gridApi?.applyTransaction({ remove: [node.data] });
  }

  buildColumnDefs(portals: DeclinePortal[], unpinned = false): ColDef<DeclineCategoryRow>[] {
    const portalCols: ColDef<DeclineCategoryRow>[] = portals.map((p) => ({
      headerName: p.name,
      colId: `portal_${p.id}`,
      flex: 1,
      minWidth: unpinned ? 140 : 168,
      autoHeight: true,
      wrapText: true,
      cellRenderer: DeclineMappingPortalCellComponent,
      cellRendererParams: { portalId: p.id },
    }));

    return [
      {
        headerName: 'Standardized category',
        colId: 'name',
        ...(unpinned ? {} : { pinned: 'left' as const }),
        width: unpinned ? 160 : 220,
        autoHeight: true,
        wrapText: true,
        cellRenderer: DeclineMappingCategoryCellComponent,
      },
      ...portalCols,
      {
        headerName: '',
        colId: 'delete',
        width: 56,
        maxWidth: 64,
        ...(unpinned ? {} : { pinned: 'right' as const }),
        suppressSizeToFit: true,
        autoHeight: true,
        cellRenderer: DeclineMappingDeleteCellComponent,
      },
    ];
  }

  setColumnDefs(columnDefs: ColDef<DeclineCategoryRow>[]): void {
    this.gridApi?.setGridOption('columnDefs', columnDefs);
  }

  refreshAllCells(): void {
    this.gridApi?.refreshCells();
  }

  refreshRowCells(rowIds: string[], portalIds: string[] = []): void {
    if (!this.gridApi || rowIds.length === 0) return;

    const rowNodes = rowIds
      .map((rowId) => this.gridApi!.getRowNode(rowId))
      .filter((rowNode): rowNode is IRowNode<DeclineCategoryRow> => !!rowNode);

    if (rowNodes.length === 0) return;

    const columns = ['name', ...portalIds.map((portalId) => `portal_${portalId}`)];
    this.gridApi.refreshCells({ rowNodes, columns });
  }

  refreshPortalCells(cells: Array<DeclineReasonMappingCell | null>): void {
    if (!this.gridApi) return;

    const rowNodesById = new Map<string, IRowNode<DeclineCategoryRow>>();
    const columnIds = new Set<string>();

    for (const cell of cells) {
      if (!cell) continue;
      const rowNode = this.gridApi.getRowNode(cell.rowId);
      if (rowNode) {
        rowNodesById.set(cell.rowId, rowNode);
        columnIds.add(`portal_${cell.portalId}`);
      }
    }

    if (rowNodesById.size === 0 || columnIds.size === 0) return;

    this.gridApi.refreshCells({
      rowNodes: Array.from(rowNodesById.values()),
      columns: Array.from(columnIds),
    });
  }
}
